import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  getUserContacts,
  getPendingRequests,
  getContact,
  canDM,
  deleteContact,
  updateContactDetails
} from '../services/contactService';
import { db } from '../config/firebase';
import { onSnapshot, collection, query, where } from 'firebase/firestore';

const ContactContext = createContext();

export function ContactProvider({ children }) {
  const { user } = useAuth();

  // State
  const [contacts, setContacts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Setup real-time listeners
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setPendingRequests([]);
      setBlockedUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const listeners = [];

    // Listen to user's contacts (accepted only)
    const contactsQuery = query(
      collection(db, 'contacts'),
      where('userId', '==', user.uid),
      where('status', '==', 'accepted')
    );

    const unsubscribeContacts = onSnapshot(
      contactsQuery,
      snapshot => {
        const contactsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContacts(contactsList);
      },
      error => {
        console.error('Error loading contacts:', error.message);
        // Gracefully handle permission errors
        setContacts([]);
      }
    );
    listeners.push(unsubscribeContacts);

    // Listen to pending requests
    const requestsQuery = query(
      collection(db, 'contactRequests'),
      where('toUserId', '==', user.uid),
      where('status', '==', 'pending')
    );

    const unsubscribeRequests = onSnapshot(
      requestsQuery,
      snapshot => {
        const requestsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPendingRequests(requestsList);
      },
      error => {
        console.error('Error loading pending requests:', error.message);
        setPendingRequests([]);
      }
    );
    listeners.push(unsubscribeRequests);

    // Listen to blocked users
    const blockedQuery = query(
      collection(db, 'blockedUsers'),
      where('blockerId', '==', user.uid)
    );

    const unsubscribeBlocked = onSnapshot(
      blockedQuery,
      snapshot => {
        const blockedList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBlockedUsers(blockedList);
      },
      error => {
        console.error('Error loading blocked users:', error.message);
        setBlockedUsers([]);
      }
    );
    listeners.push(unsubscribeBlocked);

    setLoading(false);

    return () => {
      listeners.forEach(unsub => unsub());
    };
  }, [user]);

  // Check if can DM
  const checkCanDM = async (targetUserId) => {
    if (!user) return false;
    return await canDM(user.uid, targetUserId);
  };

  // Get contact info
  const getContactInfo = async (contactId) => {
    if (!user) return null;
    return await getContact(user.uid, contactId);
  };

  // Delete a contact
  const deleteContactAction = async (contactId) => {
    if (!user) throw new Error('User not authenticated');
    return await deleteContact(user.uid, contactId);
  };

  // Update contact details (nickname, notes)
  const updateContactDetailsAction = async (contactId, updates) => {
    if (!user) throw new Error('User not authenticated');
    return await updateContactDetails(user.uid, contactId, updates);
  };

  const value = {
    contacts,
    pendingRequests,
    blockedUsers,
    loading,
    checkCanDM,
    getContactInfo,
    deleteContact: deleteContactAction,
    updateContactDetails: updateContactDetailsAction,
    contactsCount: contacts.length,
    pendingRequestsCount: pendingRequests.length
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
}

export function useContacts() {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContacts must be used within ContactProvider');
  }
  return context;
}
